import React, { useState } from 'react'
import styled from 'styled-components'
import { ConfirmButton, Modal, CurrencyInputItem, ApproveTokenForStakingButton } from '../../../../common'
import { Colors, FontWeights, INTEGRAL_SYMBOL } from '../../../../constants'
import { useEthers, useSendTransaction } from '../../../../hooks'
import { CurrencyValue } from '../../../../model'
import { getStakingDepositTransaction } from '../../../../transactions'
import { StakingOption, getStakingRewards, getStakingParams } from '../utils'
import { getButtonState } from './getButtonState'
import { StakeState, useStake } from './useStake'

interface Props {
  stakingOption: StakingOption
  onClose: () => void
  onSubmit: () => void
}

export function ConfirmDeposit({ stakingOption, onClose, onSubmit }: Props) {
  const { account, chainId } = useEthers()
  const [sending, setSending] = useState(false)
  const { stakeValue, setStakeValue, setMax, integralToken, state } = useStake(stakingOption)
  const sendTransaction = useSendTransaction()
  const buttonState = getButtonState(state, integralToken)
  const rewards = getStakingRewards(stakingOption, integralToken, stakeValue)
  const { apr, duration } = getStakingParams(stakingOption)

  async function addStake() {
    if (!account || !chainId) {
      return
    }
    setSending(true)
    try {
      await sendTransaction(
        getStakingDepositTransaction({
          chainId,
          stakingOption,
          stakeValue: CurrencyValue.fromString(integralToken, stakeValue),
          duration,
          apr,
          rewards,
        })
      )
      setSending(false)
      onSubmit()
    } catch (err) {
      setSending(false)
      if (err instanceof Error) {
        alert(err.message) // TODO: better error handling
      }
    }
  }

  return (
    <Modal title="Confirm Stake" onClose={onClose}>
      <Content>
        <CurrencyInputItem
          label="You stake"
          rate=""
          value={stakeValue}
          onValueChange={setStakeValue}
          onMax={setMax}
          currency={integralToken}
        />
        <Description>
          <label>Duration</label>
          <label>{duration}</label>
        </Description>
        <Description>
          <label>APR</label>
          <label>{apr}</label>
        </Description>
        <Description>
          <label>Total Rewards</label>
          <label>
            {rewards} {INTEGRAL_SYMBOL}
          </label>
        </Description>
        {state === StakeState.NEED_APPROVAL && (
          <ApproveTokenForStakingButton currency={integralToken} stakingOption={stakingOption} />
        )}
        <ConfirmButtonStyled
          onClick={addStake}
          disabled={buttonState.disabled || sending}
          error={buttonState.error}
          sending={sending}
          text={buttonState.text}
        />
      </Content>
    </Modal>
  )
}

const Content = styled.div`
  padding: 15px 20px;
  color: ${Colors.Black};
  > div:first-child {
    padding: 0px;
  }
`

const Description = styled.div`
  padding: 5px 0px;
  display: flex;
  justify-content: space-between;
  label {
    text-transform: uppercase;
  }
  label:first-child {
    color: ${Colors.Black};
    font-weight: ${FontWeights.Bold};
  }
  label:last-child {
    color: ${Colors.DarkGray};
    font-weight: ${FontWeights.Semibold};
  }
`

const ConfirmButtonStyled = styled(ConfirmButton)`
  margin-top: 20px;
`
